import * as React from "react";
import * as moment from "moment";
import { RouteComponentProps } from "react-router";
import { RioIf, RioApiGet, RioApiPost, RioExpandingPanel, RioResources, RioDateHelpers, RioConfirmModal, RioApiDelete } from "rio.react.all";
import { Page } from "../components/Page";
import { HttpErrorHelper } from "../helpers/HttpErrorHelper";
import { DataVineGridView } from "../components/GridView/GridView";
import { IGridViewColumn } from "../components/GridView/IGridViewColumn";
import { IGridViewAction } from "../components/GridView/IGridViewAction";
import { WarehouseClosureApiModel } from "../apiModels/WarehouseClosureApiModel";
import { WarehouseClosuresEditorModal } from "../modals/WarehouseClosuresEditorModal";

interface IWarehouseClosuresProps extends Partial<RouteComponentProps> {

}

interface IWarehouseClosuresState {
	futureClosures: Array<WarehouseClosureApiModel>;
	pastClosures: Array<WarehouseClosureApiModel>; 
	editItem: WarehouseClosureApiModel;
	deleteItem: WarehouseClosureApiModel;
}

export class WarehouseClosures extends React.Component<IWarehouseClosuresProps, IWarehouseClosuresState> {
    constructor(props: Readonly<IWarehouseClosuresProps>) {
        super(props);

        this.state = {
			futureClosures: [],
			pastClosures: [],
			editItem: null,
			deleteItem: null,
        };
	}

	columns: Array<IGridViewColumn<WarehouseClosureApiModel>> = [
		{ title: RioResources.find("WarehouseClosureName").text, displayValue: (item) => item.name, value: (item) => item.name },
		{ title: RioResources.find("DateFrom").text, displayValue: (item) => RioDateHelpers.toDateFormat(item.dateFrom, "DD/MM/YYYY"), value: (item) => item.dateFrom },
		{ title: RioResources.find("DateTo").text, displayValue: (item) => RioDateHelpers.toDateFormat(item.dateTo, "DD/MM/YYYY"), value: (item) => item.dateTo },
	];

	actions: Array<IGridViewAction<WarehouseClosureApiModel>> = [
		{ resource: RioResources.find("Edit"), onClick: item => this.handleEdit(item), hasPermission: true },
		{ resource: RioResources.find("Delete"), onClick: item => this.handleDelete(item), hasPermission: true },
	];

    render() {
        return (
			<Page heading={RioResources.find("WarehouseClosures")}>
				<div className="expandingPanel-side-by-side">
					<RioExpandingPanel
						className="noBackground showTitle"
						disableToggle={true}
						expandedDefault={true}
						title={RioResources.find("WarehouseClosuresFuture").text}
						actions={[
							{ resource: RioResources.find("New"), onClick: () => this.handleNewClicked(), hasPermission: true }
						]}
					>
						<DataVineGridView<WarehouseClosureApiModel>
							noResults={RioResources.find("NoResultsFound")}
							columns={this.columns}
							actions={this.actions}
							items={this.state.futureClosures}
							rowKey={(item: WarehouseClosureApiModel) => item.warehouseClosuresID}
						/>

					</RioExpandingPanel>

					<RioExpandingPanel
						className="noBackground showTitle"
						disableToggle={true}
						expandedDefault={true}
						title={RioResources.find("WarehouseClosuresPast").text}
						actions={[]}
					>
						<DataVineGridView<WarehouseClosureApiModel>
							noResults={RioResources.find("NoResultsFound")}
							columns={this.columns}
							actions={this.actions}
							items={this.state.pastClosures}
							rowKey={(item: WarehouseClosureApiModel) => item.warehouseClosuresID}
						/>
					</RioExpandingPanel>
				</div>


				<RioIf condition={!!this.state.editItem} then={
					<WarehouseClosuresEditorModal onClose={() => this.handleEditClosed()} data={this.state.editItem}/>
				} />

				<RioIf condition={!!this.state.deleteItem} then={
					<RioConfirmModal text={RioResources.find("DeleteWarehouseClosureConfirmMsg")} onConfirmModalClosed={(result) => this.handleDeleteConfirmed(result)} className="confirmModal" />
				} />

			</Page>
        );
    }

    componentDidMount(): void {
		this.loadData();
	}

	loadData(): void {
		RioApiGet.asPromise<Array<WarehouseClosureApiModel>>("/api/WarehouseClosures/Get")
			.then(response => {
				if (!!response) {
					this.setState({
						futureClosures: response
							.filter(x => !this.dateInPast(new Date(x.dateTo)))
							.sort((a, b) => {
								if (a.dateFrom !== b.dateFrom) {
									return a.dateFrom > b.dateFrom ? 1 : -1;
								} else {
									// If dateFrom is equal, use dateTo for secondary sorting
									return a.dateTo > b.dateTo ? 1 : -1;
								}
							}),

						pastClosures: response
							.filter(x => this.dateInPast(new Date(x.dateTo)))
							.sort((a, b) => {
								if (a.dateTo !== b.dateTo) {
									return a.dateTo < b.dateTo ? 1 : -1;
								} else {
									// If dateTo is equal, use dateFrom for secondary sorting
									return a.dateFrom < b.dateFrom ? 1 : -1;
								}
							}),
					});

				}
			})
			.catch((response) => {
				var error = HttpErrorHelper.handleHttpError(response, this, null); //handles validator errors if validator passed
			});;
	}

	dateInPast(firstDate: Date) {
		let today = new Date();
		return firstDate.setHours(0, 0, 0, 0) < today.setHours(0, 0, 0, 0);
	}

	handleNewClicked() {
		this.handleEdit({
			warehouseClosuresID: null,
			name: '',
			dateFrom: moment().toISOString(),
			dateTo: moment().toISOString()
		});
	}

	handleEdit(item: WarehouseClosureApiModel) {
		this.setState({
			editItem: item,
		});
	}

	handleEditClosed() {
		this.loadData();
		this.setState({
			editItem: null
		});
	}

	handleDelete(item: WarehouseClosureApiModel) {
		this.setState({
			deleteItem: item,
		});
	}

	handleDeleteConfirmed(result: boolean) {
		if (result) {
			RioApiDelete.asPromise<boolean>("/api/WarehouseClosures/Delete/" + this.state.deleteItem.warehouseClosuresID)
				.then(response => {
					if (!!response) {
						this.loadData();
					}
				})
				.catch((response) => {
					var error = HttpErrorHelper.handleHttpError(response, this, null); //handles validator errors if validator passed
				});
		}

		this.setState({
			deleteItem: null,
		});
	}
}
